import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Components
import Thumb from '../Thumb';

// Config
import { IMAGE_BASE_URL, POSTER_SIZE } from '../../config';

// Image
import NoImage from '../../images/no_image.jpg';

// Styles
import { Wrapper, Content, Text } from './MovieInfo.styles';

// Auth
import { useUserAuth } from '../../context/UserAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// ─── Save Button Styles ───────────────────────────────────────────────────────

const SaveArea = styled.div`
  position: absolute;
  top: 16px;
  right: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;

const SaveBtn = styled.button`
  /* Circular shape — like Spotify's play button */
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;

  /* Metallic gunmetal gradient — mimics Spotify's brushed-metal look */
  background: ${(p) =>
    p.$saved
      ? 'radial-gradient(circle at 35% 35%, #3a3a3a 0%, #1a1a1a 60%, #0d0d0d 100%)'
      : 'radial-gradient(circle at 35% 35%, #3a3a3a 0%, #1a1a1a 60%, #0d0d0d 100%)'};

  /* Neon outer glow ring */
  border: 2px solid ${(p) => (p.$saved ? '#7effb2' : '#fe88f1')};
  box-shadow: ${(p) =>
    p.$saved
      ? '0 0 0 3px rgba(126,255,178,0.25), 0 0 16px 4px rgba(126,255,178,0.45), 0 0 40px 8px rgba(126,255,178,0.18), inset 0 1px 2px rgba(255,255,255,0.12)'
      : '0 0 0 3px rgba(254,136,241,0.25), 0 0 16px 4px rgba(254,136,241,0.45), 0 0 40px 8px rgba(254,136,241,0.18), inset 0 1px 2px rgba(255,255,255,0.12)'};

  color: ${(p) => (p.$saved ? '#7effb2' : '#fe88f1')};
  transition: all 0.25s ease;

  /* Inner shine highlight — top-left specular like brushed metal */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 25%, rgba(255,255,255,0.18) 0%, transparent 60%);
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    transform: scale(1.08);
    box-shadow: ${(p) =>
      p.$saved
        ? '0 0 0 4px rgba(126,255,178,0.35), 0 0 24px 8px rgba(126,255,178,0.6), 0 0 56px 12px rgba(126,255,178,0.22), inset 0 1px 2px rgba(255,255,255,0.15)'
        : '0 0 0 4px rgba(254,136,241,0.35), 0 0 24px 8px rgba(254,136,241,0.6), 0 0 56px 12px rgba(254,136,241,0.22), inset 0 1px 2px rgba(255,255,255,0.15)'};
  }

  &:active:not(:disabled) { transform: scale(0.96); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  svg {
    width: 24px;
    height: 24px;
    /* Slight drop shadow on the icon for depth */
    filter: drop-shadow(0 0 4px currentColor);
  }
`;

const SaveFeedback = styled.span`
  font-size: 0.72rem;
  color: rgba(255,255,255,0.55);
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(6px);
  padding: 3px 10px;
  border-radius: 99px;
  white-space: nowrap;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const MovieInfo = ({ movie }) => {
  const { isLoggedIn, userToken } = useUserAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  // Check save status on mount
  useEffect(() => {
    if (!isLoggedIn || !movie?.id) return;
    fetch(`${BACKEND_URL}/my-films/${movie.id}/status`, {
      headers: { Authorization: `Bearer ${userToken}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) setSaved(d.saved); })
      .catch(() => {});
  }, [isLoggedIn, userToken, movie?.id]);

  const handleSave = async () => {
    if (!isLoggedIn) {
      navigate('/sign-in');
      return;
    }
    setSaving(true);
    try {
      if (saved) {
        // Unsave
        await fetch(`${BACKEND_URL}/my-films/${movie.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${userToken}` },
        });
        setSaved(false);
        setFeedback('Removed from My Films');
      } else {
        // Save with all metadata
        await fetch(`${BACKEND_URL}/my-films`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify({
            tmdbId: movie.id,
            title: movie.title,
            posterPath: movie.poster_path || '',
            genres: (movie.genres || []).map((g) => g.name || g),
            directors: (movie.directors || []).map((d) => d.name || d),
            actors: (movie.actors || []).slice(0, 5).map((a) => a.name || a),
            voteAverage: movie.vote_average || 0,
          }),
        });
        setSaved(true);
        setFeedback('Saved to My Films ✓');
      }
    } catch {
      setFeedback('Something went wrong');
    } finally {
      setSaving(false);
      setTimeout(() => setFeedback(''), 2500);
    }
  };

  return (
    <Wrapper backdrop={movie.backdrop_path} style={{ position: 'relative' }}>
      <Content>
        <Thumb
          poster={
            movie.poster_path
              ? `${IMAGE_BASE_URL}${POSTER_SIZE}${movie.poster_path}`
              : NoImage
          }
          clickable={false}
        />
        <Text>
          <h1>{movie.title}</h1>
          <h3>PLOT</h3>
          <p>{movie.overview}</p>

          <div className='rating-directors'>
            <div>
              <h3>RATING</h3>
              <div className="score">{movie.vote_average}</div>
            </div>
            <div className="director">
              <h3>DIRECTOR{(movie.directors || []).length > 1 ? 'S' : ''}</h3>
              {(movie.directors || []).map(item => (
                <div key={item.credit_id}>{item.name}</div>
              ))}
            </div>
          </div>
        </Text>
      </Content>

      {/* Save button — top right of the hero */}
      <SaveArea>
        <SaveBtn $saved={saved} disabled={saving} onClick={handleSave} aria-label={saved ? 'Remove from My Films' : 'Save to My Films'}>
          {saved ? (
            /* Checkmark — saved state */
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            /* Plus sign — unsaved state */
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          )}
        </SaveBtn>
        {feedback && <SaveFeedback>{feedback}</SaveFeedback>}
      </SaveArea>
    </Wrapper>
  );
};

MovieInfo.propTypes = {
  movie: PropTypes.object,
};

export default MovieInfo;
